import React, { useEffect, useState } from 'react';
import { Search, User, Building2 } from 'lucide-react';
import { ResponsiveModal } from '../ui/ResponsiveModal';
import { usePartnerStore } from '../../lib/stores/partnerStore';
import type { ExternalPartner } from '../../lib/types';

interface PartnerSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectPartner: (partner: ExternalPartner) => void;
  selectedPartnerId?: string | null;
}

export function PartnerSelector({ isOpen, onClose, onSelectPartner, selectedPartnerId }: PartnerSelectorProps) {
  const { partners, loading, fetchPartners } = usePartnerStore();
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchPartners();
    }
  }, [isOpen, fetchPartners]);

  const filteredPartners = partners.filter(partner =>
    partner.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (partner.company || '').toLowerCase().includes(searchTerm.toLowerCase())
  ).filter(partner => partner.status === 'active');

  const handlePartnerSelect = (partner: ExternalPartner) => {
    onSelectPartner(partner);
    onClose();
  };

  const handleRemovePartner = () => {
    // Create a dummy partner with id null to indicate removal
    onSelectPartner({ id: null } as any);
    onClose();
  };

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={onClose}
      title="Seleziona Partner"
      size="md"
      mobileHeight="80vh"
    >
      {/* Search */}
        <div className="p-4 border-b border-[rgba(55,53,47,0.09)] dark:border-[#3F4447]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#787774] dark:text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Cerca per nome o azienda..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-[#F7F7F5] dark:bg-[#373C3F] border border-[rgba(55,53,47,0.09)] dark:border-[#3F4447] rounded-md text-[#37352F] dark:text-white placeholder:text-[#787774] dark:placeholder:text-gray-500 focus:outline-none focus:ring-1 focus:ring-[#2383E2]"
            />
          </div>
        </div>

        {/* Partner List */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#37352F] dark:border-white mx-auto"></div>
              <p className="text-[#787774] dark:text-gray-400 mt-2">Caricamento partner...</p>
            </div>
          ) : filteredPartners.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-[#787774] dark:text-gray-400">
                {searchTerm ? 'Nessun partner trovato' : 'Nessun partner disponibile'}
              </p>
            </div>
          ) : (
            <div className="space-y-0.5">
              {filteredPartners.map((partner) => (
                <button
                  key={partner.id}
                  onClick={() => handlePartnerSelect(partner)}
                  className={`w-full px-2 py-1.5 rounded-md transition-colors text-left ${
                    selectedPartnerId === partner.id
                      ? 'bg-[rgba(55,53,47,0.08)] dark:bg-white/[0.08]'
                      : 'hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/[0.04]'
                  }`}
                  type="button"
                >
                  <div className="flex items-center space-x-2.5">
                    <div className="flex-shrink-0">
                      <div className="w-6 h-6 bg-[#F1F1EF] dark:bg-[#454B4E] rounded-full flex items-center justify-center">
                        <User className="h-3 w-3 text-[#787774] dark:text-gray-400" />
                      </div>
                    </div>
                    <div className="flex-grow min-w-0 flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-sm font-medium text-[#37352F] dark:text-white truncate">
                          {partner.name}
                        </span>
                        {partner.company && (
                          <span className="text-xs text-[#787774] dark:text-gray-400 truncate hidden sm:inline">
                            {partner.company}
                          </span>
                        )}
                      </div>
                      {partner.partner_type && (
                        <span className="flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-[#F1F1EF] dark:bg-[#454B4E] text-[#787774] dark:text-gray-300">
                          {partner.partner_type.replace('_', ' ')}
                        </span>
                      )}
                    </div>
                  </div>
                  {partner.email && (
                    <div className="text-xs text-[#787774] dark:text-gray-500 ml-[34px] truncate mt-0.5">
                      {partner.email}
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-[rgba(55,53,47,0.09)] dark:border-[#3F4447] bg-[#F7F7F5] dark:bg-[#373C3F]">
          <div className="flex justify-between">
            {selectedPartnerId && (
              <button
                onClick={handleRemovePartner}
                className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors"
                type="button"
              >
                Rimuovi Partner
              </button>
            )}
            <button
              onClick={onClose}
              className="ml-auto px-4 py-2 text-sm font-medium text-[#787774] dark:text-gray-400 hover:text-[#37352F] dark:hover:text-white transition-colors"
              type="button"
            >
              Annulla
            </button>
          </div>
        </div>
    </ResponsiveModal>
  );
}